import React, { useState, useEffect } from 'react';
import { Input, Card, Row, Col, Typography, Collapse, Button, Tag, Divider, Breadcrumb, Space } from 'antd';
import { 
  SearchOutlined, 
  QuestionCircleOutlined, 
  BookOutlined, 
  ToolOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  MessageOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  RightOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AnimatedSection } from './components/AnimatedSection';
import { theme } from './components/theme';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

const Help = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleNavigate = (path) => {
    if (path.startsWith('http')) {
      window.location.href = path;
    } else if (path.startsWith('/')) {
      window.open(path, '_self');
    } else {
      const element = document.getElementById(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Help categories
  const categories = [
    { key: 'all', label: 'All Topics', icon: <BookOutlined />, count: 24 },
    { key: 'getting-started', label: 'Getting Started', icon: <PlayCircleOutlined />, count: 6 },
    { key: 'diff-insight', label: 'Diff Insight', icon: <ToolOutlined />, count: 8 },
    { key: 'crm', label: 'CRM Platform', icon: <QuestionCircleOutlined />, count: 5 },
    { key: 'migration', label: 'RCA Migration', icon: <FileTextOutlined />, count: 5 },
  ];

  // FAQ data
  const faqData = [
    {
      category: 'getting-started',
      question: 'How do I get started with Aptify?',
      answer: 'Getting started is simple! First, create an account by clicking the "Sign Up" button. Once registered, you can access our suite of tools including Diff Insight, CRM Platform, and RCA Migration. Each tool has guided tutorials to help you begin.',
      tags: ['account', 'setup', 'beginner']
    },
    {
      category: 'getting-started',
      question: 'What are the system requirements?',
      answer: 'Aptify works on any modern web browser (Chrome, Firefox, Safari, Edge). No additional software installation is required. For optimal performance, we recommend using the latest browser versions with JavaScript enabled.',
      tags: ['requirements', 'browser', 'technical']
    },
    {
      category: 'diff-insight',
      question: 'How secure is my Salesforce data in Diff Insight?',
      answer: 'Your data security is our top priority. Diff Insight uses a zero-storage architecture - we never store your Salesforce data on our servers. All comparisons are performed in real-time and data is immediately discarded after processing.',
      tags: ['security', 'salesforce', 'data-protection']
    },
    {
      category: 'diff-insight',
      question: 'What types of Salesforce data can I compare?',
      answer: 'Diff Insight supports comparison of objects, fields, workflows, validation rules, page layouts, permission sets, profiles, and custom metadata. You can compare between different Salesforce orgs or different versions of the same org.',
      tags: ['comparison', 'salesforce', 'objects']
    },
    {
      category: 'crm',
      question: 'Can I integrate the CRM with other tools?',
      answer: 'Yes! Our CRM platform offers robust API integration with 500+ business applications including email marketing tools, accounting software, and productivity suites. We also provide pre-built connectors for popular platforms.',
      tags: ['integration', 'api', 'third-party']
    },
    {
      category: 'migration',
      question: 'How does RCA Migration ensure data integrity?',
      answer: 'RCA Migration includes comprehensive data validation checks, automated backup creation, rollback capabilities, and detailed migration logs. Our system performs multiple validation passes to ensure 100% data integrity throughout the migration process.',
      tags: ['data-integrity', 'validation', 'backup']
    }
  ];

  // Help articles
  const helpArticles = [
    {
      category: 'getting-started',
      title: 'Quick Start Guide',
      description: 'Get up and running with Aptify in 5 minutes',
      readTime: '5 min read',
      tags: ['beginner', 'setup']
    },
    {
      category: 'getting-started',
      title: 'Account Setup & Configuration',
      description: 'Complete guide to setting up your Aptify account',
      readTime: '8 min read',
      tags: ['account', 'configuration']
    },
    {
      category: 'diff-insight',
      title: 'Connecting Your Salesforce Org',
      description: 'Step-by-step guide to connect Salesforce with Diff Insight',
      readTime: '10 min read',
      tags: ['salesforce', 'connection']
    },
    {
      category: 'diff-insight',
      title: 'Understanding Comparison Reports',
      description: 'Learn how to read and interpret Diff Insight reports',
      readTime: '12 min read',
      tags: ['reports', 'analysis']
    },
    {
      category: 'crm',
      title: 'Setting Up Your Sales Pipeline',
      description: 'Configure your sales stages and pipeline workflow',
      readTime: '15 min read',
      tags: ['pipeline', 'sales']
    },
    {
      category: 'migration',
      title: 'Planning Your Migration',
      description: 'Best practices for successful RCA migrations',
      readTime: '20 min read',
      tags: ['planning', 'best-practices']
    }
  ];

  // Filter articles based on search and category
  useEffect(() => {
    let filtered = helpArticles;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredArticles(filtered);
  }, [searchTerm, selectedCategory]);

  const supportOptions = [
    {
      title: 'Live Chat',
      description: 'Get instant help from our support team',
      icon: <MessageOutlined />,
      action: 'Start Chat',
      available: 'Available 24/7'
    },
    {
      title: 'Email Support',
      description: 'Send us a detailed message',
      icon: <MailOutlined />,
      action: 'Send Email',
      available: 'Response within 2 hours'
    },
    {
      title: 'Phone Support',
      description: 'Speak directly with our experts',
      icon: <PhoneOutlined />,
      action: 'Call Now',
      available: 'Mon-Fri, 9AM-6PM EST'
    }
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.neutral.white,
        fontFamily: theme.typography.fontFamily.base,
      }}
    >
      <Header onNavigate={handleNavigate} />

      <main style={{ paddingTop: '72px' }}>
        {/* Hero Section */}
        <section
          style={{
            background: `linear-gradient(135deg, ${theme.colors.primary.navy} 0%, ${theme.colors.primary.blue} 100%)`,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
            textAlign: 'center',
            color: theme.colors.neutral.white,
          }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title
                level={1}
                style={{
                  color: theme.colors.neutral.white,
                  fontSize: theme.typography.fontSize['5xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  marginBottom: theme.spacing.md,
                }}
              >
                How can we help you?
              </Title>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <Paragraph
                style={{
                  fontSize: theme.typography.fontSize.xl,
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: theme.spacing['2xl'],
                }}
              >
                Find answers, tutorials, and get support for all Aptify products
              </Paragraph>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={200}>
              <Search
                placeholder="Search for help articles, tutorials, or FAQs..."
                size="large"
                style={{
                  maxWidth: '600px',
                  margin: '0 auto',
                }}
                prefix={<SearchOutlined />}
                onChange={(e) => setSearchTerm(e.target.value)}
                enterButton
              />
            </AnimatedSection>
          </div>
        </section>

        {/* Breadcrumb */}
        <section style={{ padding: `${theme.spacing.lg} ${theme.spacing.lg} 0` }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <Breadcrumb>
              <Breadcrumb.Item href="/">
                <HomeOutlined /> Home
              </Breadcrumb.Item>
              <Breadcrumb.Item>Help Center</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </section>

        {/* Categories */}
        <section style={{ padding: `${theme.spacing.xl} ${theme.spacing.lg}` }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title level={2} style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                Browse by Category
              </Title>
            </AnimatedSection>

            <Row gutter={[24, 24]}>
              {categories.map((category, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={category.key}>
                  <AnimatedSection animation="fade-up" delay={index * 100}>
                    <Card
                      hoverable
                      style={{
                        textAlign: 'center',
                        border: selectedCategory === category.key ? `2px solid ${theme.colors.primary.orange}` : '1px solid #f0f0f0',
                        borderRadius: theme.borderRadius.lg,
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedCategory(category.key)}
                      bodyStyle={{ padding: theme.spacing.lg }}
                    >
                      <div
                        style={{
                          fontSize: '32px',
                          color: theme.colors.primary.orange,
                          marginBottom: theme.spacing.md,
                        }}
                      >
                        {category.icon}
                      </div>
                      <Title level={4} style={{ marginBottom: theme.spacing.sm }}>
                        {category.label}
                      </Title>
                      <Text type="secondary">{category.count} articles</Text>
                    </Card>
                  </AnimatedSection>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Popular Articles */}
        <section
          style={{
            backgroundColor: theme.colors.neutral.gray50,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title level={2} style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                {selectedCategory === 'all' ? 'Popular Articles' : `${categories.find(c => c.key === selectedCategory)?.label} Articles`}
              </Title>
            </AnimatedSection>

            <Row gutter={[24, 24]}>
              {filteredArticles.map((article, index) => (
                <Col xs={24} md={12} lg={8} key={index}>
                  <AnimatedSection animation="fade-up" delay={index * 100}>
                    <Card
                      hoverable
                      style={{
                        height: '100%',
                        borderRadius: theme.borderRadius.lg,
                        boxShadow: theme.shadows.sm,
                      }}
                      actions={[
                        <Button type="link" icon={<RightOutlined />}>
                          Read Article
                        </Button>
                      ]}
                    >
                      <Card.Meta
                        title={
                          <Title level={4} style={{ marginBottom: theme.spacing.sm }}>
                            {article.title}
                          </Title>
                        }
                        description={
                          <div>
                            <Paragraph style={{ marginBottom: theme.spacing.md }}>
                              {article.description}
                            </Paragraph>
                            <div style={{ marginBottom: theme.spacing.sm }}>
                              <Text type="secondary">{article.readTime}</Text>
                            </div>
                            <Space wrap>
                              {article.tags.map(tag => (
                                <Tag key={tag} color="blue">{tag}</Tag>
                              ))}
                            </Space>
                          </div>
                        }
                      />
                    </Card>
                  </AnimatedSection>
                </Col>
              ))}
            </Row>

            {filteredArticles.length === 0 && (
              <div style={{ textAlign: 'center', padding: theme.spacing['2xl'] }}>
                <Title level={4} type="secondary">
                  No articles found
                </Title>
                <Paragraph type="secondary">
                  Try adjusting your search terms or browse different categories
                </Paragraph>
              </div>
            )}
          </div>
        </section>

        {/* FAQ Section */}
        <section style={{ padding: `${theme.spacing['4xl']} ${theme.spacing.lg}` }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title level={2} style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                Frequently Asked Questions
              </Title>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <Collapse
                ghost
                expandIconPosition="right"
                style={{
                  background: 'transparent',
                }}
              >
                {faqData.map((faq, index) => (
                  <Panel
                    header={
                      <Title level={4} style={{ margin: 0 }}>
                        {faq.question}
                      </Title>
                    }
                    key={index}
                    style={{
                      marginBottom: theme.spacing.md,
                      backgroundColor: theme.colors.neutral.white,
                      borderRadius: theme.borderRadius.lg,
                      border: `1px solid ${theme.colors.neutral.gray200}`,
                    }}
                  >
                    <Paragraph style={{ fontSize: theme.typography.fontSize.base }}>
                      {faq.answer}
                    </Paragraph>
                    <Space wrap style={{ marginTop: theme.spacing.md }}>
                      {faq.tags.map(tag => (
                        <Tag key={tag} color="orange">{tag}</Tag>
                      ))}
                    </Space>
                  </Panel>
                ))}
              </Collapse>
            </AnimatedSection>
          </div>
        </section>

        {/* Contact Support */}
        <section
          style={{
            background: `linear-gradient(135deg, ${theme.colors.accent.peach} 0%, ${theme.colors.neutral.white} 100%)`,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title level={2} style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                Still need help?
              </Title>
              <Paragraph
                style={{
                  textAlign: 'center',
                  fontSize: theme.typography.fontSize.lg,
                  marginBottom: theme.spacing['2xl'],
                  maxWidth: '600px',
                  margin: `0 auto ${theme.spacing['2xl']}`,
                }}
              >
                Our support team is here to help you succeed. Choose the option that works best for you.
              </Paragraph>
            </AnimatedSection>

            <Row gutter={[32, 32]} justify="center">
              {supportOptions.map((option, index) => (
                <Col xs={24} md={8} key={index}>
                  <AnimatedSection animation="fade-up" delay={index * 100}>
                    <Card
                      hoverable
                      style={{
                        textAlign: 'center',
                        borderRadius: theme.borderRadius.lg,
                        height: '100%',
                      }}
                      bodyStyle={{ padding: theme.spacing.xl }}
                    >
                      <div
                        style={{
                          fontSize: '48px',
                          color: theme.colors.primary.orange,
                          marginBottom: theme.spacing.lg,
                        }}
                      >
                        {option.icon}
                      </div>
                      <Title level={3} style={{ marginBottom: theme.spacing.md }}>
                        {option.title}
                      </Title>
                      <Paragraph style={{ marginBottom: theme.spacing.lg }}>
                        {option.description}
                      </Paragraph>
                      <Text type="secondary" style={{ display: 'block', marginBottom: theme.spacing.lg }}>
                        {option.available}
                      </Text>
                      <Button
                        type="primary"
                        size="large"
                        style={{
                          backgroundColor: theme.colors.primary.orange,
                          borderColor: theme.colors.primary.orange,
                        }}
                      >
                        {option.action}
                      </Button>
                    </Card>
                  </AnimatedSection>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
};

export default Help;